import React from 'react';
import { CommonButton } from '~/common/components/CommonButton';
import { Spinner } from '~/common/components/Spinner';
import { MODAL_TYPES, MODIFIER_TYPE, TRIAL_TYPES } from '~/common/consts';
import { ModalContentType, SitecoreTrialContentType } from '~/common/types';
import styles from './Trial.module.css';
import { useAppStore } from '~/common/store/appStore';
import { useShallow } from 'zustand/react/shallow';
import { isModalModifier } from '~/common/util/isModal';

type TrialButtonSwitchType = {
  formSubmitting: boolean;
  data: SitecoreTrialContentType;
  modalContent?: ModalContentType;
  handleCallback?: (step: number) => void;
  goToNextStep?: () => void;
  handleBackClick?: () => void;
  step: number;
  is2Step: boolean;
};

type ViewType = 'MODAL_STEP_1' | 'MODAL_STEP_2' | 'MODAL_SINGLE' | 'TWO_STEP_1' | 'TWO_STEP_2' | 'SINGLE';

export const TrialButtonSwitch = ({
  formSubmitting,
  data,
  modalContent,
  handleCallback,
  goToNextStep,
  handleBackClick,
  step,
  is2Step,
}: TrialButtonSwitchType) => {
  const { modifier, trialType, ctaButton, ctaClassNameModifier } = useAppStore(
    useShallow((state) => ({
      modifier: state.modifier,
      trialType: state.trialType,
      ctaButton: state.ctaButton,
      ctaClassNameModifier: state.ctaClassNameModifier,
    }))
  );

  const view: ViewType = (() => {
    if (isModalModifier(modifier) && modalContent) {
      if (trialType === TRIAL_TYPES.BUSINESS) {
        return step === 1 ? 'MODAL_STEP_1' : 'MODAL_STEP_2';
      } else {
        return 'MODAL_SINGLE';
      }
    }
    if (is2Step) {
      return step === 1 ? 'TWO_STEP_1' : 'TWO_STEP_2';
    }
    return 'SINGLE';
  })();

  const renderSpinner = (content: React.ReactNode) =>
    formSubmitting ? <Spinner colorBottom={typeof ctaClassNameModifier === 'string' && ctaClassNameModifier.length > 0 ? 'rgba(0,0,0,0.5)' : '#751a17'} colorTop="#fff" /> : content;

  const hero = modifier === MODIFIER_TYPE.HERO;

  const ABTestPlaceholderString = () => {
    return trialType === TRIAL_TYPES.PREMIUM || trialType === TRIAL_TYPES.TEAMS ? 'Start trial' : data.sendButton;
  };

  return (
    <>
      {(() => {
        switch (view) {
          case 'MODAL_STEP_1':
            // modal + step === 1
            return (
              <div className={styles.buttonHolder}>
                <CommonButton type="button" link onClick={() => handleCallback(0)}>
                  {modalContent.backButton}
                </CommonButton>

                <CommonButton type="submit" id="EMAIL_SUBMIT" disabled={formSubmitting} onClick={goToNextStep}>
                  {renderSpinner(modalContent.sendButton)}
                </CommonButton>
              </div>
            );

          case 'MODAL_STEP_2':
            // modal + step !== 1
            return (
              <div className={styles.buttonHolder}>
                <CommonButton type="button" link onClick={handleBackClick}>
                  {modalContent.backButton}
                </CommonButton>

                <CommonButton id="FORM_SUBMIT" type="submit" disabled={formSubmitting}>
                  {renderSpinner(data.sendButton2)}
                </CommonButton>
              </div>
            );

          case 'MODAL_SINGLE':
            // modal + step !== 1
            return (
              <div className={styles.buttonHolder}>
                <CommonButton type="button" link onClick={() => handleCallback(0)}>
                  {modalContent.backButton}
                </CommonButton>

                <CommonButton id="FORM_SUBMIT" type="submit" disabled={formSubmitting}>
                  {renderSpinner(data.sendButton)}
                </CommonButton>
              </div>
            );

          case 'TWO_STEP_1':
            // 2-step (non-modal) + step === 1
            return (
              <CommonButton type="submit" id="EMAIL_SUBMIT" disabled={formSubmitting} modifier={ctaClassNameModifier ?? undefined} onClick={goToNextStep}>
                {renderSpinner(hero && ctaButton ? ctaButton : data.sendButton)}
              </CommonButton>
            );

          case 'TWO_STEP_2':
            // 2-step (non-modal) + step !== 1
            return (
              <div className={styles.buttonHolder}>
                <CommonButton type="button" link onClick={handleBackClick}>
                  {data.backButton}
                </CommonButton>

                <CommonButton id="FORM_SUBMIT" type="submit" disabled={formSubmitting} modifier={ctaClassNameModifier ?? undefined}>
                  {renderSpinner(data.sendButton2)}
                </CommonButton>
              </div>
            );

          case 'SINGLE':
          default:
            // 1-step form
            return (
              <CommonButton id="FORM_SUBMIT" type="submit" disabled={formSubmitting} modifier={ctaClassNameModifier ?? undefined}>
                {renderSpinner(hero ? (ctaButton || ABTestPlaceholderString()) : data.sendButton)}
              </CommonButton>
            );
        }
      })()}
    </>
  );
};
